import { FomanticShim, PartialNullable } from "../../Extensions";
import { JNative, JNativeStatic } from "../../JNative";
import { PropertyChange } from "../../PropertyChange";
import { Property, PropertyBoolean, PropertyInt32 } from "../../RadObject";
import { Control, ControlPropertyValues, IControl } from "./Control";
import { NAMESPACE } from "./_Namespace";

import "./ProgressBar.scss";

export type ProgressBarPropertyValues =
    ControlPropertyValues
    & {
        invertStyles: boolean;
        total: number;
        value: number;
    };

export type ProgressBarProperties = {
    [name in keyof ProgressBarPropertyValues]: Property<ProgressBarPropertyValues[name]>
};

export interface IProgressBar extends IControl, ProgressBarProperties
{
}

export class ProgressBar extends Control implements IProgressBar
{
    // #region Static

    public static override classNamespace = NAMESPACE + "ProgressBar";
    public static override className = "ProgressBar";

    // #region Overrides

    public static override createPropertyDefaults(): PartialNullable<ProgressBarPropertyValues>
    {
        return {
            ...super.createPropertyDefaults(),
            ...{
                invertStyles: false,
                text: "Loaded {value} of {total}",
                value: 0,
            }
        };
    }

    // #endregion

    // #endregion

    // #region Instance

    // #region Global Variables

    protected $$progress?: JNative;
    protected showDate: Date;

    // #endregion

    // #region Properties

    public invertStyles!: PropertyBoolean;
    public total!: PropertyInt32;
    public value!: PropertyInt32;

    // #endregion

    // #region Constructor

    constructor(propertyValues?: PartialNullable<ProgressBarPropertyValues>)
    {
        super(propertyValues);

        this.showDate = new Date();

        this.addCssClass("rad-control-progress-bar");

        // Trigger Property Changes
        this.value.triggerChange();
    }

    // #endregion

    // #region Public Methods

    reset()
    {
        FomanticShim(this.$$progress).progress("reset");
        this.value.set(0);
    }

    // #endregion

    // #region Protected Methods

    protected _invertStylesChanged(change: PropertyChange<boolean>)
    {
        if (change.currentValue && !this.$$progress?.hasClass("inverted"))
            this.$$progress?.addClass("inverted");
        else if (!change.currentValue)
            this.$$progress?.removeClass("inverted");
    }
    protected _totalChanged(change: PropertyChange<number>)
    {
        this._updateProgressBarData();
    }
    protected _updateProgressBarData()
    {
        FomanticShim(this.$$progress).progress({
            total: this.total.get(),
            value: this.value.get(),
            text: {
                active: this.text.get()
            }
        });
    }
    protected _valueChanged(change: PropertyChange<number>)
    {
        this.showDate = new Date();
        this._updateProgressBarData();
    }

    // #endregion

    // #region Private Methods

    // #endregion

    // #region Overrides

    protected override __initializeProperties(propertyValues: ProgressBarPropertyValues)
    {
        super.__initializeProperties(propertyValues);

        this.invertStyles = new PropertyBoolean(this, propertyValues.invertStyles, this._invertStylesChanged);
        this.total = new PropertyInt32(this, propertyValues.total, this._totalChanged);
        this.value = new PropertyInt32(this, propertyValues.value, this._valueChanged);
    }

    protected override _createElement()
    {
        const
            $$element = super._createElement();

        this.$$progress =
            JNativeStatic.create$$({
                nodeName: "div",
                attributes: { "class": "ui blue small progress" },
                children: [
                    { nodeName: "div", attributes: { "class": "bar" } },
                    { nodeName: "div", attributes: { "class": "label" } }
                ],
            }).appendTo($$element);

        FomanticShim(this.$$progress).progress();

        if (this.invertStyles.get())
            this.$$progress.addClass("inverted");

        this._updateProgressBarData();

        return $$element;
    }
    protected override _textChanged(change: PropertyChange<string>)
    {
        super._textChanged(change);

        this._updateProgressBarData();
    }

    // #endregion

    // #endregion
}